import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

/**
 * Component for protecting routes based on user roles or menu permissions
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string[]} props.allowedRoles - Array of role codes that can access this route
 * @param {string[]} props.requiredPermissions - Array of menu codes required to access this route
 * @param {string} props.redirectPath - Path to redirect to if unauthorized (defaults to /dashboard)
 */
const RoleProtectedRoute = ({ 
  children, 
  allowedRoles = [], 
  requiredPermissions = [], 
  redirectPath = "/dashboard" 
}) => {
  const location = useLocation();
  const { isAuthenticated, user } = useSelector(state => state.auth);
  const { menuPermissions } = useSelector(state => state.permissions);
  
  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  
  // If no role or permission restrictions are specified, allow access
  if (allowedRoles.length === 0 && requiredPermissions.length === 0) {
    return children;
  }

  // Check if user has any of the allowed roles
  const hasAllowedRole = allowedRoles.length === 0 || 
    (user?.primary_role_id && allowedRoles.includes(user.primary_role_id));
  
  // Check if user has all required permissions
  const hasRequiredPermissions = requiredPermissions.length === 0 || 
    requiredPermissions.every(permission => {
      // Check in flat structure
      const hasDirectPermission = menuPermissions.some(menu => 
        menu.id === permission || menu.menu_code === permission
      );
      
      // Check in hierarchical structure
      const hasNestedPermission = menuPermissions.some(menu => {
        if (menu.children && menu.children.length > 0) {
          return menu.children.some(child => 
            child.id === permission || child.menu_code === permission
          );
        }
        return false;
      });
      
      return hasDirectPermission || hasNestedPermission;
    });
  
  // Only allow access if user has the required permissions
  // Changed from OR to AND to strictly enforce permissions
  if (hasRequiredPermissions) {
    return children;
  }
  
  // Redirect to specified path if unauthorized
  return <Navigate to={redirectPath} replace state={{ from: location }} />;
};

export default RoleProtectedRoute;
